import { Link } from "@tanstack/react-router";
import { Check } from "lucide-react";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";

import { SectionHeading, SETTLE_SPRING } from "./SectionHeading";

const FEATURES = [
  "featOne",
  "featTwo",
  "featThree",
  "featFour",
  "featFive",
  "featSix",
  "featSeven",
] as const;

export function StartSection() {
  const { t } = useTranslation("home");

  // Own surface: pricing followed two plain-cream sections and read as more
  // page rather than the destination. cream-deep makes it a stop.
  return (
    <section
      id="pricing"
      className="scroll-mt-32 bg-home-cream-deep px-5 py-20 md:scroll-mt-24 md:py-24"
    >
      <div className="mx-auto max-w-5xl">
        <SectionHeading kicker={t("start.kicker")} title={t("start.title")} />
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.12, duration: 0.7 }}
          className="mx-auto mt-5 max-w-xl text-center font-body text-lg leading-relaxed text-home-ink/70"
        >
          {t("start.subhead")}
        </motion.p>

        {/* One plan, one card */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ ...SETTLE_SPRING, delay: 0.1 }}
          className="relative mx-auto mt-10 max-w-md"
        >
          {/* Soft flat halo behind the card */}
          <div
            aria-hidden="true"
            className="absolute -inset-4 rounded-[44px] bg-home-butter opacity-70 blur-xl"
          />

          <article className="relative rounded-[36px] bg-white p-9 shadow-[0_28px_60px_-28px_rgba(33,31,28,0.45)]">
            <h3 className="font-display text-2xl font-bold text-home-ink">
              {t("start.planName")}
            </h3>

            <p className="mt-4 flex items-baseline gap-2">
              <span className="font-display text-6xl font-bold tracking-tight text-home-ink">
                {t("start.price")}
              </span>
              <span className="font-body text-lg font-medium text-home-ink/65">
                {t("start.period")}
              </span>
            </p>
            <p className="mt-5 font-display text-lg font-medium text-home-ink/80">
              {t("start.line")}
            </p>

            <ul className="mt-6 space-y-3">
              {FEATURES.map((key) => (
                <li key={key} className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-home-butter">
                    <Check className="h-3 w-3 text-home-ink" strokeWidth={3} />
                  </span>
                  <span className="font-body text-[15px] leading-snug text-home-ink/80">
                    {t(`start.${key}`)}
                  </span>
                </li>
              ))}
            </ul>

            <Link
              to="/login"
              search={{ pendingCode: undefined }}
              className="mt-8 block rounded-full bg-home-yellow px-6 py-4 text-center font-body text-base font-bold text-home-ink shadow-[0_10px_28px_-12px_rgba(33,31,28,0.4)] transition-all duration-300 ease-out hover:-translate-y-0.5 hover:shadow-[0_16px_34px_-12px_rgba(33,31,28,0.45)]"
            >
              {t("start.cta")}
            </Link>
          </article>
        </motion.div>

        <p className="mx-auto mt-10 max-w-xl text-center font-body text-sm leading-relaxed text-home-ink/65">
          {t("start.footnote")}
        </p>
      </div>
    </section>
  );
}
